import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { usePageTitle as usePageTitleContext } from '../contexts/PageTitleContext';
import { useAuth } from '../contexts/AuthContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  ArrowDownRightIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  PencilIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
  CodeBracketIcon,
  PaintBrushIcon,
  EyeIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import { EmailTemplateDesignEditor, type EmailTemplateDesignEditorHandle } from '../components/EmailTemplateDesignEditor';

type TemplateType = 'email_verification' | 'email_update' | 'password_reset' | 'invitation_single_file' | 'invitation_multi_file' | 'team_invitation' | 'device_registration' | 'file_access_request' | 'file_access_request_response';
type TemplateStatus = 'active' | 'draft' | 'inactive';

const devicePresets = [
  { name: 'Desktop', width: 1280, height: 800 },
  { name: 'Tablet', width: 768, height: 1024 },
  { name: 'Mobile', width: 375, height: 667 },
  { name: 'iPhone 14 Pro', width: 393, height: 852 },
  { name: 'iPhone SE', width: 375, height: 667 },
  { name: 'Galaxy S21', width: 360, height: 800 },
  { name: 'iPad', width: 768, height: 1024 },
  { name: 'iPad Pro', width: 1024, height: 1366 },
];

const templateTypeNames: Record<TemplateType, string> = {
  email_verification: 'Email Verification',
  email_update: 'Email Update',
  password_reset: 'Password Reset',
  invitation_single_file: 'Single File Invitation',
  invitation_multi_file: 'Multiple Files Invitation',
  team_invitation: 'Team Invitation',
  file_access_request: 'File Access Request',
  file_access_request_response: 'File Access Request Response',
  device_registration: 'Device Registration',
};

const templateDescriptions: Record<TemplateType, string> = {
  email_verification: 'Sent when a user registers and needs to verify their email address with an OTP code.',
  email_update: 'Sent when a user requests to update their email address with an OTP code.',
  password_reset: 'Sent when a user requests to reset their password with an OTP code.',
  invitation_single_file: 'Sent when a single secure file is shared with a non-registered recipient.',
  invitation_multi_file: 'Sent when multiple secure files are shared with a non-registered recipient.',
  team_invitation: 'Sent when a user is invited to join an organisation. Contains accept-invite link.',
  file_access_request: 'Sent to file owner when a recipient requests additional access to a file.',
  file_access_request_response: 'Sent to requester when file owner responds to their access request (accepted/rejected).',
  device_registration: 'Sent when a user needs to register a new device with an OTP code.',
};

// Available company variables (mapped to actual company fields)
const companyVariables = [
  { key: 'company_logo_url', companyField: 'logo_url', label: 'Logo URL', description: 'Company logo image URL' },
  { key: 'company_name', companyField: 'name', label: 'Company Name', description: 'Full company name' },
  { key: 'company_domain', companyField: 'domain', label: 'Domain', description: 'Company domain' },
  { key: 'company_description', companyField: 'description', label: 'Description', description: 'Company description' },
  { key: 'company_website', companyField: 'website', label: 'Website', description: 'Company website URL' },
  { key: 'company_app_version', companyField: 'app_version', label: 'App Version', description: 'Application version' },
  { key: 'company_contact_person', companyField: 'contact_person', label: 'Contact Person', description: 'Contact person name' },
  { key: 'company_contact_person_email', companyField: 'contact_person_email', label: 'Contact Person Email', description: 'Contact person email' },
  { key: 'company_contact_email', companyField: 'contact_email', label: 'Contact Email', description: 'General contact email' },
  { key: 'company_address', companyField: 'address', label: 'Address', description: 'Company address' },
];

interface EmailTemplate {
  id: string;
  company_id?: string;
  type: TemplateType;
  status: TemplateStatus;
  name: string; // Human-readable name for the template type
  description: string; // Description of what this template is used for
  subject: string;
  html_content: string;
  created_at?: string;
  updated_at?: string;
  variables?: string[];
}

// Default templates with standardized design

// Template List View
const TemplateListView: React.FC<{ 
  templates: EmailTemplate[];
  onTemplateClick: (id: string) => void;
  onClone: (template: EmailTemplate) => void;
  onPublish: (template: EmailTemplate) => void;
  onDelete: (template: EmailTemplate) => void;
  sortBy: 'type' | 'status';
  sortOrder: 'asc' | 'desc';
  onSortChange: (field: 'type' | 'status') => void;
}> = ({ templates, onTemplateClick, onClone, onPublish, onDelete, sortBy, sortOrder, onSortChange }) => {
  const getStatusColor = (status: TemplateStatus) => {
    switch (status) {
      case 'active': return 'text-green-600 dark:text-green-400';
      case 'draft': return 'text-yellow-600 dark:text-yellow-400';
      case 'inactive': return 'text-gray-600 dark:text-gray-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusBgColor = (status: TemplateStatus) => {
    switch (status) {
      case 'active': return 'bg-green-100 dark:bg-green-900';
      case 'draft': return 'bg-yellow-100 dark:bg-yellow-900';
      case 'inactive': return 'bg-gray-100 dark:bg-gray-800';
      default: return 'bg-gray-100 dark:bg-gray-800';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 shadow rounded-lg">
      {/* Sorting controls */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort by:</span>
          <button
            onClick={() => onSortChange('type')}
            className={`text-sm px-3 py-1 rounded ${
              sortBy === 'type'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            Type {sortBy === 'type' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
          <button
            onClick={() => onSortChange('status')}
            className={`text-sm px-3 py-1 rounded ${
              sortBy === 'status'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {templates.length} template{templates.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Template list */}
      <div className="divide-y divide-gray-200 dark:divide-gray-800">
        {templates.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <div className="text-gray-500 dark:text-gray-400">
              <p className="text-lg font-medium mb-2">No email templates defined</p>
              <p className="text-sm">Currently there are no email templates available. Templates will be automatically created when the system initializes.</p>
            </div>
          </div>
        ) : (
          templates.map((template) => (
            <div key={template.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    <div className={`w-2 h-2 rounded-full ${
                      template.status === 'active' ? 'bg-green-500' :
                      template.status === 'draft' ? 'bg-yellow-500' :
                      'bg-gray-400'
                    }`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => onTemplateClick(template.id)}
                        className="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 truncate"
                      >
                        {template.name || templateTypeNames[template.type as TemplateType] || template.type}
                      </button>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBgColor(template.status)} ${getStatusColor(template.status)}`}>
                        {template.status}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 truncate">
                      {template.subject}
                    </div>
                    <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                      {template.description || templateDescriptions[template.type as TemplateType] || 'No description available'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  {template.status !== 'active' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onPublish(template);
                      }}
                      className="inline-flex items-center p-2 border border-transparent rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      title="Publish (set as active)"
                    >
                      <ArrowUpTrayIcon className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onClone(template);
                    }}
                    className="inline-flex items-center p-2 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    title="Clone as draft"
                  >
                    <DocumentDuplicateIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onTemplateClick(template.id);
                    }}
                    className="inline-flex items-center p-2 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    title="Edit"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  {(template.status === 'draft' || template.status === 'inactive') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(template);
                      }}
                      className="inline-flex items-center p-2 border border-transparent rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Template Detail/Edit View
const TemplateDetailView: React.FC<{
  template: EmailTemplate;
  onBack: () => void;
  onSave: (template: EmailTemplate) => Promise<void>;
  onClone: (template: EmailTemplate) => void;
}> = ({ template, onBack, onSave, onClone }) => {
  const { user } = useAuth();
  const { setHasUnsavedChanges, setOnSave, setIsSubmitting } = useUnsavedChanges();
  const [currentTemplate, setCurrentTemplate] = useState<EmailTemplate>(template);
  const [initialTemplate, setInitialTemplate] = useState<EmailTemplate>(JSON.parse(JSON.stringify(template))); // Deep copy
  // Ref so Save uses latest html_content even when design editor sends updates asynchronously (link edits, etc.)
  const latestHtmlContentRef = useRef(template.html_content);

  // Set page title when viewing a specific template
  const { setPageTitle } = usePageTitleContext();
  useEffect(() => {
    const typeName = template.name || templateTypeNames[template.type as TemplateType] || template.type;
    const description = template.description || templateDescriptions[template.type as TemplateType] || 'Email template';
    setPageTitle(typeName, description);
    return () => {
      // Reset to default when component unmounts
      setPageTitle('Email Templates', 'Manage and customize email templates for your organization');
    };
  }, [template.type, template.name, template.description, setPageTitle]);

  // Update currentTemplate and initialTemplate when template prop changes (e.g., after save)
  useEffect(() => {
    setCurrentTemplate(template);
    setInitialTemplate(JSON.parse(JSON.stringify(template))); // Deep copy
    latestHtmlContentRef.current = template.html_content;
  }, [template]);

  // Detect changes (status changes are not allowed from UI, so we don't check it)
  useEffect(() => {
    const hasChanges = 
      currentTemplate.subject !== initialTemplate.subject ||
      currentTemplate.html_content !== initialTemplate.html_content;
    
    setHasUnsavedChanges(hasChanges);
    
    if (hasChanges) {
      setOnSave(() => async () => {
        setIsSubmitting(true);
        try {
          // Use ref so we save latest html_content (design editor updates it async; Save may run before state commit)
          const templateToSave = {
            ...currentTemplate,
            html_content: latestHtmlContentRef.current,
            status: initialTemplate.status, // Keep original status
          };
          await onSave(templateToSave);
          toast.success('Template saved successfully');
          // Update initial template after save
          setInitialTemplate(JSON.parse(JSON.stringify(templateToSave)));
          setHasUnsavedChanges(false);
        } catch (err: any) {
          toast.error(err.response?.data?.error || 'Failed to save template');
        } finally {
          setIsSubmitting(false);
        }
      });
    } else {
      setOnSave(null);
    }
    
    return () => {
      setHasUnsavedChanges(false);
      setOnSave(null);
    };
  }, [currentTemplate, initialTemplate, setHasUnsavedChanges, setOnSave, setIsSubmitting, onSave]);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewWidth, setPreviewWidth] = useState(1280);
  const [previewHeight, setPreviewHeight] = useState(800);
  const [isResizing, setIsResizing] = useState(false);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [company, setCompany] = useState<any>(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [enabledCompanyVariables, setEnabledCompanyVariables] = useState<Set<string>>(new Set(['company_logo_url']));
  const modalRef = useRef<HTMLDivElement>(null);
  const resizeStartX = useRef<number>(0);
  const resizeStartY = useRef<number>(0);
  const resizeStartWidth = useRef<number>(1280);
  const resizeStartHeight = useRef<number>(800);
  const justFinishedResizing = useRef<boolean>(false);
  const [companyVariablesExpanded, setCompanyVariablesExpanded] = useState(false);
  const companyVariablesRef = useRef<HTMLDivElement>(null);
  const [viewMode, setViewMode] = useState<'design' | 'code'>('design');
  const [designResetKey, setDesignResetKey] = useState(0);
  const [canUndo, setCanUndo] = useState(false);
  const designEditorRef = useRef<EmailTemplateDesignEditorHandle>(null);

  /** Dedupe identical style/link tags so design saves do not grow unbounded. */
  const extractStyleBlocks = useCallback((html: string): string => {
    const styleMatches = html.match(/<style\b[^>]*>[\s\S]*?<\/style>/gi) || [];
    const linkMatches = html.match(/<link\b[^>]*rel=["']stylesheet["'][^>]*>/gi) || [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const block of [...styleMatches, ...linkMatches]) {
      const key = block.replace(/\s+/g, ' ').trim();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(block);
      }
    }
    return out.join('\n');
  }, []);

  /** Remove leading style/link blocks from serialized body (they are re-attached from extractStyleBlocks). */
  const stripLeadingStyleAndLinkBlocks = useCallback((bodyHtml: string): string => {
    let rest = bodyHtml;
    for (let i = 0; i < 500; i++) {
      const t = rest.trimStart();
      const styleM = t.match(/^<style\b[^>]*>[\s\S]*?<\/style>/i);
      if (styleM) {
        rest = t.slice(styleM[0].length);
        continue;
      }
      const linkM = t.match(/^<link\b[^>]*rel=["']stylesheet["'][^>]*>/i);
      if (linkM) {
        rest = t.slice(linkM[0].length);
        continue;
      }
      break;
    }
    return rest.trimStart();
  }, []);

  // Close company variables dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (companyVariablesRef.current && !companyVariablesRef.current.contains(event.target as Node)) {
        setCompanyVariablesExpanded(false);
      }
    };

    if (companyVariablesExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [companyVariablesExpanded]);

  // Fetch company data
  useEffect(() => {
    const fetchCompany = async () => {
      if (!user?.company_id) return;
      
      try {
        setCompanyLoading(true);
        const response = await apiService.get(`/companies/${user.company_id}`);
        setCompany(response.data);
      } catch (err) {
        console.error('Failed to fetch company:', err);
        toast.error('Failed to load company information');
      } finally {
        setCompanyLoading(false);
      }
    };
    
    fetchCompany();
  }, [user?.company_id]);

  // Handle preview mode change
  useEffect(() => {
    if (previewMode === 'mobile') {
      setPreviewWidth(375);
      setPreviewHeight(667);
    } else {
      setPreviewWidth(1280);
      setPreviewHeight(800);
    }
  }, [previewMode]);

  // Handle device preset selection
  const handleDevicePreset = (preset: typeof devicePresets[0]) => {
    setPreviewWidth(preset.width);
    setPreviewHeight(preset.height);
  };

  /** Single fallback logo URL for Governate branding when company has no logo. */
  const GOVERNATE_LOGO_URL = 'https://admin.governate.com/logo-gov.svg';

  // Normalize only: replace literal logo URLs with {{company_logo_url}} and strip legacy "G" fallback so logo is a single editable img.
  const normalizeTemplateHtmlForDesign = useCallback(
    (text: string): string => {
      let result = text;
      if (enabledCompanyVariables.has('company_logo_url')) {
        result = result.split(GOVERNATE_LOGO_URL).join('{{company_logo_url}}');
        if (company?.logo_url) {
          const companyLogo = String(company.logo_url);
          if (companyLogo) result = result.split(companyLogo).join('{{company_logo_url}}');
        }
      }
      // Remove legacy logo fallback: onerror on img and the following "G" div so the logo is a single <img> editable in design (Image URL / Alt).
      result = result.replace(
        /\s+onerror="this\.style\.display='none';\s*this\.nextElementSibling\.style\.display='flex';"/gi,
        ''
      );
      // Remove legacy "G" fallback div (display:none box with "G" text) so logo is a single img.
      result = result.replace(
        /<div\s+[^>]*style="[^"]*display\s*:\s*none[^"]*"[^>]*>\s*G\s*<\/div>/gi,
        ''
      );
      return result;
    },
    [company?.logo_url, enabledCompanyVariables]
  );

  // Full document for design editor. Other variables stay as {{...}}. We substitute only {{company_logo_url}}
  // with the actual URL so the img loads (otherwise the browser treats it as a relative path and breaks).
  // On save, reverseReplaceForDesign puts {{company_logo_url}} back so the DB keeps the placeholder.
  const getDesignEditorHtml = useCallback(() => {
    let bodyContent = normalizeTemplateHtmlForDesign(currentTemplate.html_content);
    const logoDisplayUrl = company?.logo_url ? String(company.logo_url) : GOVERNATE_LOGO_URL;
    bodyContent = bodyContent.split('{{company_logo_url}}').join(logoDisplayUrl);
    return `<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>body { margin: 0; padding: 0; overflow-x: hidden; }</style></head><body>${bodyContent}</body></html>`;
  }, [currentTemplate.html_content, normalizeTemplateHtmlForDesign, company?.logo_url]);

  // Memoize so design editor only receives new HTML when template or company logo actually changes (e.g. when company loads).
  const designEditorHtml = useMemo(() => getDesignEditorHtml(), [getDesignEditorHtml]);

  // Replace variables with sample data for preview (modal and sent emails only)
  const replaceVariables = useCallback((text: string): string => {
    let result = text;
    if (enabledCompanyVariables.has('company_logo_url')) {
      result = result.split(GOVERNATE_LOGO_URL).join('{{company_logo_url}}');
      if (company?.logo_url) {
        const companyLogo = String(company.logo_url);
        if (companyLogo) result = result.split(companyLogo).join('{{company_logo_url}}');
      }
    }
    const sampleData: Record<string, string> = {
      email: 'user@example.com',
      otp: '123456',
      fileName: 'document.pdf',
      senderName: 'John Doe',
      deviceName: 'iPhone 15 Pro',
      fileCount: '3',
      companyImageUrl: 'https://via.placeholder.com/150',
      company_logo_url: GOVERNATE_LOGO_URL,
      fileNames: '<div style="display: flex; align-items: center; padding: 12px; background: white; border-radius: 8px; margin-bottom: 8px; border: 1px solid #e9ecef;"><div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 14px; margin-right: 12px;">1</div><div style="color: #495057; font-size: 15px; font-weight: 500;">document1.pdf</div></div><div style="display: flex; align-items: center; padding: 12px; background: white; border-radius: 8px; margin-bottom: 8px; border: 1px solid #e9ecef;"><div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 14px; margin-right: 12px;">2</div><div style="color: #495057; font-size: 15px; font-weight: 500;">document2.pdf</div></div><div style="display: flex; align-items: center; padding: 12px; background: white; border-radius: 8px; border: 1px solid #e9ecef;"><div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 14px; margin-right: 12px;">3</div><div style="color: #495057; font-size: 15px; font-weight: 500;">document3.pdf</div></div>',
    };
    if (company) {
      companyVariables.forEach(({ key, companyField }) => {
        if (enabledCompanyVariables.has(key)) {
          const value = company[companyField] || '';
          if (key === 'company_logo_url') {
            sampleData[key] = value ? String(value) : GOVERNATE_LOGO_URL;
          } else {
            sampleData[key] = value ? String(value) : `{{${key}}}`;
          }
        }
      });
    }
    const allVariables = [
      ...(currentTemplate.variables || []),
      'companyImageUrl',
      ...Array.from(enabledCompanyVariables),
    ];
    allVariables.forEach((variable) => {
      const regex = new RegExp(`\\{\\{${variable}\\}\\}`, 'g');
      result = result.replace(regex, sampleData[variable] || `{{${variable}}}`);
    });
    return result;
  }, [currentTemplate.variables, company, enabledCompanyVariables]);

  // Get preview HTML (with substituted sample data) for the Preview modal only
  const getPreviewHtml = useCallback(() => {
    const html = replaceVariables(currentTemplate.html_content);
    return `<html><head><meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"><style>body { margin: 0; padding: 0; overflow-x: hidden; }</style></head><body>${html}</body></html>`;
  }, [currentTemplate.html_content, replaceVariables]);

  // Reverse-replace displayed values back to {{variable}} when saving from design view.
  const reverseReplaceForDesign = useCallback(
    (bodyInnerHtml: string): string => {
      let result = bodyInnerHtml;
      const replacements: { value: string; placeholder: string }[] = [];
      if (company) {
        companyVariables.forEach(({ key, companyField }) => {
          if (enabledCompanyVariables.has(key)) {
            const value = company[companyField];
            if (value != null && value !== '') {
              replacements.push({ value: String(value), placeholder: `{{${key}}}` });
            }
          }
        });
      }
      if (enabledCompanyVariables.has('company_logo_url')) {
        replacements.push({ value: GOVERNATE_LOGO_URL, placeholder: '{{company_logo_url}}' });
      }
      replacements.sort((a, b) => b.value.length - a.value.length);
      replacements.forEach(({ value, placeholder }) => {
        result = result.split(value).join(placeholder);
      });
      return result;
    },
    [company, enabledCompanyVariables]
  );

  // Handle resize start
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!modalRef.current) return;
    
    setIsResizing(true);
    const rect = modalRef.current.getBoundingClientRect();
    resizeStartX.current = e.clientX;
    resizeStartY.current = e.clientY;
    resizeStartWidth.current = rect.width;
    resizeStartHeight.current = rect.height;
  }, []);

  // Handle mouse move for resizing
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if ((e.buttons & 1) === 0) {
        setIsResizing(false);
        justFinishedResizing.current = true;
        setTimeout(() => {
          justFinishedResizing.current = false;
        }, 100);
        return;
      }
      
      const deltaX = e.clientX - resizeStartX.current;
      const deltaY = e.clientY - resizeStartY.current;
      
      const newWidth = Math.max(400, Math.min(window.innerWidth * 0.8, resizeStartWidth.current + deltaX));
      const newHeight = Math.max(300, Math.min(window.innerHeight * 0.9, resizeStartHeight.current + deltaY));
      
      setPreviewWidth(newWidth);
      setPreviewHeight(newHeight);
    };

    const handleMouseUp = (e: MouseEvent) => {
      e.stopPropagation();
      setIsResizing(false);
      justFinishedResizing.current = true;
      setTimeout(() => {
        justFinishedResizing.current = false;
      }, 100);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleTemplateChange = (field: 'subject' | 'html_content', value: string) => {
    if (field === 'html_content') latestHtmlContentRef.current = value;
    setCurrentTemplate((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const copyVariable = async (variable: string) => {
    try {
      await navigator.clipboard.writeText(`{{${variable}}}`);
      toast.success(`Copied {{${variable}}} to clipboard`);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Listen for preview button click from header
  useEffect(() => {
    const handlePreviewClick = () => {
      setIsPreviewOpen(true);
    };

    window.addEventListener('open-email-preview', handlePreviewClick);
    return () => {
      window.removeEventListener('open-email-preview', handlePreviewClick);
    };
  }, []);

  return (
    <div className="flex flex-col min-h-0 flex-1 overflow-y-auto">
      {/* Top row: Company Variables, Design/Code (centered), Preview, Status, Clone */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Company Variables Dropdown */}
          {companyLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            </div>
          ) : company ? (
            <div className="relative" ref={companyVariablesRef}>
              <button
                type="button"
                onClick={() => setCompanyVariablesExpanded(!companyVariablesExpanded)}
                className="px-3 py-2 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 min-w-[200px] justify-between"
              >
                <span>Company Variables</span>
                {companyVariablesExpanded ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </button>
              {companyVariablesExpanded && (
                <div className="absolute z-10 mt-1 w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg max-h-64 overflow-y-auto">
                  <div className="p-2 space-y-1">
                    {companyVariables.map((varInfo) => {
                      const isEnabled = enabledCompanyVariables.has(varInfo.key);
                      const value = company[varInfo.companyField] || '';
                      return (
                        <label
                          key={varInfo.key}
                          className="flex items-start space-x-3 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                        >
                          <div className="flex items-center h-5 mt-0.5">
                            <input
                              type="checkbox"
                              checked={isEnabled}
                              onChange={(e) => {
                                const newSet = new Set(enabledCompanyVariables);
                                if (e.target.checked) {
                                  newSet.add(varInfo.key);
                                } else {
                                  newSet.delete(varInfo.key);
                                }
                                setEnabledCompanyVariables(newSet);
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {varInfo.label}
                                </span>
                                <span className="ml-2 text-xs font-mono text-gray-500 dark:text-gray-400">
                                  {'{{' + varInfo.key + '}}'}
                                </span>
                              </div>
                              {isEnabled && (
                                <CheckIcon className="w-4 h-4 text-green-600" />
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {varInfo.description}
                            </p>
                            {value && (
                              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 truncate" title={String(value)}>
                                Value: {String(value)}
                              </p>
                            )}
                            {!value && (
                              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                                No value set in company profile
                              </p>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
        {/* Design | Code toggle - centered in editor area */}
        <div className="flex-1 flex justify-center min-w-0">
          <div className="flex rounded-md p-0.5 bg-gray-100 dark:bg-gray-800">
            <button
              type="button"
              onClick={() => {
                if (viewMode === 'code') setDesignResetKey((k) => k + 1);
                setViewMode('design');
              }}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === 'design'
                  ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <PaintBrushIcon className="w-4 h-4" />
              Design
            </button>
            <button
              type="button"
              onClick={() => setViewMode('code')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === 'code'
                  ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <CodeBracketIcon className="w-4 h-4" />
              Code
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => designEditorRef.current?.undo()}
            disabled={!canUndo || viewMode !== 'design'}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Undo last action"
          >
            <ArrowUturnLeftIcon className="w-4 h-4" />
            <span>Undo</span>
          </button>
          <button
            type="button"
            onClick={() => setIsPreviewOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
            title="Preview email"
          >
            <EyeIcon className="w-4 h-4" />
            <span>Preview</span>
          </button>
          <span className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium ${
            currentTemplate.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
            currentTemplate.status === 'draft' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
            'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
          }`}>
            {currentTemplate.status}
          </span>
          <button
            type="button"
            onClick={() => onClone(currentTemplate)}
            className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            title="Clone as draft"
          >
            <DocumentDuplicateIcon className="w-4 h-4 mr-2" />
            Clone
          </button>
        </div>
      </div>

      {/* Editor area - min height so template is visible; page scrolls when viewport is short */}
      <div className="flex-1 min-h-[calc(100vh-10rem)] mt-2">
        {viewMode === 'design' && companyLoading && (
          <div className="flex-1 min-h-[200px] flex items-center justify-center bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading company settings…</p>
            </div>
          </div>
        )}
        {viewMode === 'design' && !companyLoading && (
          <EmailTemplateDesignEditor
            ref={designEditorRef}
            previewHtml={designEditorHtml}
            onHtmlChange={(bodyInnerHtml) => {
              const withoutDupHead = stripLeadingStyleAndLinkBlocks(bodyInnerHtml);
              const preservedStyles = extractStyleBlocks(currentTemplate.html_content);
              const bodyWithStyles = preservedStyles
                ? `${preservedStyles}\n${withoutDupHead}`
                : withoutDupHead;
              const withPlaceholders = reverseReplaceForDesign(bodyWithStyles);
              latestHtmlContentRef.current = withPlaceholders;
              setCurrentTemplate((prev) =>
                prev.html_content === withPlaceholders ? prev : { ...prev, html_content: withPlaceholders }
              );
            }}
            resetKey={designResetKey}
            onUndoAvailability={setCanUndo}
          />
        )}

        {viewMode === 'code' && (
          <>
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Available Variables (click to copy)
            </label>
            <div className="flex flex-wrap gap-2">
              {(currentTemplate.variables || []).map((variable) => (
                <button
                  key={`html-${variable}`}
                  onClick={() => copyVariable(variable)}
                  className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md text-sm font-medium hover:bg-blue-100 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-800"
                  title={`Click to copy {{${variable}}}`}
                >
                  {`{{${variable}}}`}
                </button>
              ))}
              {Array.from(enabledCompanyVariables).map((variable) => {
                const varInfo = companyVariables.find(v => v.key === variable);
                return (
                  <button
                    key={`company-${variable}`}
                    onClick={() => copyVariable(variable)}
                    className="px-3 py-1.5 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded-md text-sm font-medium hover:bg-orange-100 dark:hover:bg-orange-900/30 border border-orange-200 dark:border-orange-800"
                    title={`Click to copy {{${variable}}}${varInfo ? ` - ${varInfo.description}` : ''}`}
                  >
                    {`{{${variable}}}`}
                  </button>
                );
              })}
              <button
                onClick={() => copyVariable('companyImageUrl')}
                className="px-3 py-1.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-md text-sm font-medium hover:bg-green-100 dark:hover:bg-green-900/30 border border-green-200 dark:border-green-800"
                title="Click to copy {{companyImageUrl}}"
              >
                {'{{companyImageUrl}}'}
              </button>
            </div>
          </div>

        {/* Subject Line */}
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Subject
            </label>
            <input
              type="text"
              value={currentTemplate.subject}
              onChange={(e) => handleTemplateChange('subject', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="Email subject line"
            />
          </div>

        <textarea
          value={currentTemplate.html_content}
          onChange={(e) => handleTemplateChange('html_content', e.target.value)}
          className="w-full min-h-[60vh] px-4 py-3 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-sm resize-y focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Enter HTML template here..."
          spellCheck={false}
        />
          </>
        )}
      </div>

      {/* Preview Modal - same as before */}
      {isPreviewOpen && (() => {
        const modalRoot = document.getElementById('modal-root');
        if (!modalRoot) return null;

        const modalContent = (
          <>
            {/* Device Controls Sidebar */}
            <div 
              className="fixed left-0 top-0 bottom-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-[10000] overflow-y-auto"
              style={{ width: '256px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Device Selection
                </h3>
                
                <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1 mb-4">
                  <button
                    onClick={() => setPreviewMode('desktop')}
                    className={`flex-1 p-2 rounded-md transition-all ${
                      previewMode === 'desktop'
                        ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <ComputerDesktopIcon className="w-5 h-5 mx-auto" />
                  </button>
                  <button
                    onClick={() => setPreviewMode('mobile')}
                    className={`flex-1 p-2 rounded-md transition-all ${
                      previewMode === 'mobile'
                        ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <DevicePhoneMobileIcon className="w-5 h-5 mx-auto" />
                  </button>
                </div>

                {previewMode === 'desktop' && (
                  <div className="space-y-1">
                    {devicePresets.filter(p => p.name === 'Desktop' || p.name === 'Tablet' || p.name === 'iPad' || p.name === 'iPad Pro').map((preset) => (
                      <button
                        key={preset.name}
                        onClick={() => handleDevicePreset(preset)}
                        className={`w-full text-left px-3 py-2 text-xs rounded-md transition-colors ${
                          previewWidth === preset.width && previewHeight === preset.height
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {preset.name} ({preset.width}×{preset.height})
                      </button>
                    ))}
                  </div>
                )}

                {previewMode === 'mobile' && (
                  <div className="space-y-1">
                    {devicePresets.filter(p => p.name !== 'Desktop' && p.name !== 'Tablet' && p.name !== 'iPad' && p.name !== 'iPad Pro').map((preset) => (
                      <button
                        key={preset.name}
                        onClick={() => handleDevicePreset(preset)}
                        className={`w-full text-left px-3 py-2 text-xs rounded-md transition-colors ${
                          previewWidth === preset.width && previewHeight === preset.height
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {preset.name} ({preset.width}×{preset.height})
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Backdrop and Modal */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center z-[9999] backdrop-blur-sm"
              style={{ left: '256px' }}
              onClick={(e) => {
                if (!isResizing && !justFinishedResizing.current) {
                  setIsPreviewOpen(false);
                }
              }}
            >
              <div
                ref={modalRef}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl flex flex-col relative"
                style={{
                  width: `${previewWidth}px`,
                  height: `${previewHeight}px`,
                  maxWidth: 'calc(100vw - 256px - 48px)',
                  maxHeight: '90vh',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="w-full h-full overflow-hidden relative">
                  <iframe
                    srcDoc={getPreviewHtml()}
                    className="w-full h-full border-0"
                    title="Email Preview"
                    key={`${currentTemplate.html_content}-${currentTemplate.subject}-${previewWidth}`}
                  />
                </div>

                <div
                  className="absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize z-20 bg-gray-200 dark:bg-gray-700 hover:bg-blue-500 dark:hover:bg-blue-600 rounded-tl-lg flex items-center justify-center group"
                  onMouseDown={handleResizeStart}
                  style={{ cursor: 'nwse-resize' }}
                  title="Drag to resize"
                >
                  <ArrowDownRightIcon className="w-4 h-4 text-gray-600 dark:text-gray-300 group-hover:text-white" />
                </div>
              </div>
            </div>
          </>
        );

        return createPortal(modalContent, modalRoot as HTMLElement);
      })()}
    </div>
  );
};

// Main EmailTemplates Component
const EmailTemplates: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  
  // Set page title only for list view - detail view sets its own title via usePageTitleContext
  const { setPageTitle: setPageTitleMain } = usePageTitleContext();
  useEffect(() => {
    if (!id) {
      setPageTitleMain('Email Templates', 'Manage and customize email templates for your organization');
    }
  }, [id, setPageTitleMain]);
  
  const [allTemplates, setAllTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'type' | 'status'>('type');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);

  // Fetch all templates from backend
  const fetchTemplates = useCallback(async () => {
    // For sysadmins, fetch system templates; for others, fetch company templates
    const isSysAdmin = user?.role === 'sysadmin';
    const companyId = isSysAdmin ? 'system' : user?.company_id;
    
    if (!companyId) return;
    
    try {
      setLoading(true);
      const response = await apiService.get(`/companies/${companyId}/email-templates`);
      const fetchedTemplates = response.data as EmailTemplate[] | null | undefined;
      // Ensure we always have an array, even if API returns null/undefined
      setAllTemplates(Array.isArray(fetchedTemplates) ? fetchedTemplates : []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      toast.error('Failed to load email templates');
      // Set to empty array on error to prevent crashes
      setAllTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [user?.company_id, user?.role]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleTemplateClick = (templateId: string) => {
    navigate(`/email-templates/${templateId}`);
  };

  const handleBack = () => {
    navigate('/email-templates');
  };

  const handleSave = async (template: EmailTemplate) => {
    // For sysadmins, save as system templates (company_id: null); for others, use their company_id
    const isSysAdmin = user?.role === 'sysadmin';
    const companyId = isSysAdmin ? null : user?.company_id;
    
    if (!isSysAdmin && !companyId) {
      throw new Error('Company ID is required');
    }

    const payload = {
      company_id: companyId,
      type: template.type,
      status: template.status,
      subject: template.subject,
      html_content: template.html_content,
    };

    if (template.id) {
      // Update existing template
      await apiService.patch(`/email-templates/${template.id}`, payload);
    } else {
      // Create new template
      await apiService.post('/email-templates', payload);
    }

    // Refresh templates list
    await fetchTemplates();
  };

  const handleClone = async (template: EmailTemplate) => {
    const isSysAdmin = user?.role === 'sysadmin';
    const companyId = isSysAdmin ? null : user?.company_id;
    
    if (!isSysAdmin && !companyId) {
      toast.error('Company ID is required');
      return;
    }

    const payload = {
      company_id: companyId,
      type: template.type,
      status: 'draft' as TemplateStatus,
      subject: template.subject,
      html_content: template.html_content,
    };

    try {
      await apiService.post('/email-templates', payload);
      toast.success('Template cloned as draft');
      await fetchTemplates();
    } catch (err) {
      console.error('Failed to clone template:', err);
      toast.error('Failed to clone template');
    }
  };

  const handlePublish = async (template: EmailTemplate) => {
    const isSysAdmin = user?.role === 'sysadmin';
    const companyId = isSysAdmin ? null : user?.company_id;
    
    if (!isSysAdmin && !companyId) {
      toast.error('Company ID is required');
      return;
    }

    // Only send status update - backend will handle deactivating other active templates
    const payload = {
      status: 'active' as TemplateStatus,
    };

    try {
      await apiService.patch(`/email-templates/${template.id}`, payload);
      toast.success('Template published (other active templates of this type were deactivated)');
      await fetchTemplates();
    } catch (err: any) {
      console.error('Failed to publish template:', err);
      toast.error(err.response?.data?.error || 'Failed to publish template');
    }
  };

  // Handle delete click - open modal
  const handleDeleteClick = (template: EmailTemplate) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!templateToDelete) return;

    setDeletingTemplateId(templateToDelete.id);
    try {
      await apiService.delete(`/email-templates/${templateToDelete.id}`);
      toast.success('Template deleted');
      await fetchTemplates();
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete template:', err);
      toast.error(err.response?.data?.error || 'Failed to delete template');
    } finally {
      setDeletingTemplateId(null);
    }
  };

  // Sort templates - ensure allTemplates is always an array
  const sortedTemplates = (Array.isArray(allTemplates) ? allTemplates : []).sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'type') {
      comparison = a.type.localeCompare(b.type);
    } else {
      comparison = a.status.localeCompare(b.status);
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Show detail view if id is specified
  if (id) {
    const template = (Array.isArray(allTemplates) ? allTemplates : []).find(t => t.id === id);
    if (template) {
      return (
        <TemplateDetailView
          template={template}
          onBack={handleBack}
          onSave={handleSave}
          onClone={handleClone}
        />
      );
    }
  }

  // Show list view
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <TemplateListView
            templates={sortedTemplates}
            onTemplateClick={handleTemplateClick}
            onClone={handleClone}
            onPublish={handlePublish}
            onDelete={handleDeleteClick}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={(field) => {
              if (sortBy === field) {
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
              } else {
                setSortBy(field);
                setSortOrder('asc');
              }
            }}
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && templateToDelete && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
              onClick={() => !deletingTemplateId && setShowDeleteModal(false)}
            ></div>

            {/* Modal */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      Delete Template
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete this template? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={deletingTemplateId === templateToDelete.id}
                  className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {deletingTemplateId === templateToDelete.id ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setTemplateToDelete(null);
                  }}
                  disabled={deletingTemplateId === templateToDelete.id}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default EmailTemplates;
